import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogInIcon, MailIcon, ShieldCheckIcon, ArrowLeftIcon } from 'lucide-react';
import { LoadingScreen } from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';
const LOGIN_IMAGES = ['https://cdn.magicpatterns.com/uploads/8Tni1q1GBnHeKwTh5tNMwY/rabbit_1.jpg', 'https://cdn.magicpatterns.com/uploads/brjGpNxkg6WrCpMb1PvBpa/rabbit_2.jpg'];
export function Login() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [step, setStep] = useState<'login' | 'mfa' | 'loading'>('login');
  const [email, setEmail] = useState('simon.verraest@siamtrading.th');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [heroImage] = useState(() => LOGIN_IMAGES[Math.floor(Math.random() * LOGIN_IMAGES.length)]);
  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);
  // Auto-focus first MFA input when entering MFA step
  useEffect(() => {
    if (step === 'mfa') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);
  const getMaskedEmail = () => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.charAt(0) + '•••' + (local.length > 1 ? local.charAt(local.length - 1) : '');
    return `${masked}@${domain}`;
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('mfa');
    setResendCountdown(30);
  };
  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('loading');
    setTimeout(() => {
      login();
      navigate('/overview');
    }, 2000);
  };
  const handleMfaInput = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...mfaCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setMfaCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleMfaKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...mfaCode];
      newCode[index - 1] = '';
      setMfaCode(newCode);
    }
  };
  const handleResend = () => {
    if (resendCountdown > 0) return;
    setMfaCode(['', '', '', '', '', '']);
    setResendCountdown(30);
    inputRefs.current[0]?.focus();
  };
  const isCodeComplete = mfaCode.every(digit => digit !== '');
  if (step === 'loading') {
    return <LoadingScreen data-id="element-9175" />;
  }
  return <div className="min-h-screen flex bg-white" data-id="element-9176">
      {/* Left Side - Image & Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gray-900" data-id="element-9177">
        <img src={heroImage} alt="Thailand" className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-9178" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" data-id="element-9179" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" data-id="element-9180">
          <div data-id="element-9181">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-16 w-auto object-contain bg-[#0F1B2D] px-4 py-3 rounded-lg" data-id="element-9182" />
          </div>
          <div className="max-w-xl" data-id="element-9183">
            <h1 className="text-4xl font-bold text-white mb-4 leading-tight" data-id="element-9184">
              Powering Thailand's Digital Commerce
            </h1>
            <p className="text-lg text-gray-200" data-id="element-9185">
              The complete payment solution for modern merchants. Manage
              transactions, track settlements, and grow your business with
              confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form / MFA */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center px-6 py-12 sm:px-12" data-id="element-9186">
        <div className="w-full max-w-md" data-id="element-9187">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8" data-id="element-9188">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-10 w-auto object-contain" data-id="element-9189" />
          </div>

          {step === 'login' && <>
              <div className="mb-10 text-center lg:text-left" data-id="element-9190">
                <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-9191">
                  Merchant Portal
                </h2>
                <p className="text-gray-600" data-id="element-9192">
                  Sign in to manage your business payments
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6" data-id="element-9193">
                <div data-id="element-9194">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9195">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="simon.verraest@siamtrading.th" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F7941D] focus:border-[#F7941D] outline-none transition-colors bg-gray-50 focus:bg-white" required data-id="element-9196" />
                </div>

                <div data-id="element-9197">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9198">
                    Password
                  </label>
                  <input type="password" defaultValue="••••••••" placeholder="••••••••" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F7941D] focus:border-[#F7941D] outline-none transition-colors bg-gray-50 focus:bg-white" required data-id="element-9199" />
                </div>

                <div className="flex items-center justify-between" data-id="element-9200">
                  <label className="flex items-center cursor-pointer" data-id="element-9201">
                    <input type="checkbox" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300 rounded cursor-pointer" data-id="element-9202" />
                    <span className="ml-2 text-sm text-gray-600" data-id="element-9203">
                      Remember me
                    </span>
                  </label>
                  <button type="button" className="text-sm text-[#F7941D] hover:text-[#D97B06] font-medium" data-id="element-9204">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" className="w-full bg-[#F7941D] hover:bg-[#D97B06] text-white font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" data-id="element-9205">
                  <LogInIcon size={18} className="mr-2" data-id="element-9206" />
                  Sign In
                </button>
              </form>

              <div className="mt-8 text-center lg:text-left" data-id="element-9207">
                <p className="text-sm text-gray-600" data-id="element-9208">
                  Don't have a merchant account?{' '}
                  <button className="text-[#F7941D] hover:text-[#D97B06] font-medium" data-id="element-9209">
                    Contact Sales
                  </button>
                </p>
              </div>
            </>}

          {step === 'mfa' && <>
              <button type="button" onClick={() => {
            setStep('login');
            setMfaCode(['', '', '', '', '', '']);
          }} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-8 transition-colors" data-id="element-9210">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-9211" />
                Back to sign in
              </button>

              <div className="text-center mb-8" data-id="element-9212">
                <div className="w-16 h-16 bg-[#F7941D]/10 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-9213">
                  <MailIcon size={28} className="text-[#F7941D]" data-id="element-9214" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-9215">
                  Check your email
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-9216">
                  We've sent a 6-digit verification code to
                </p>
                <p className="text-gray-900 font-medium text-sm mt-1" data-id="element-9217">
                  {getMaskedEmail()}
                </p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-6" data-id="element-9218">
                <div data-id="element-9219">
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center" data-id="element-9220">
                    Enter verification code
                  </label>
                  <div className="flex justify-center gap-3" data-id="element-9221">
                    {mfaCode.map((digit, index) => <input key={index} ref={el => {
                  inputRefs.current[index] = el;
                }} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleMfaInput(index, e.target.value)} onKeyDown={e => handleMfaKeyDown(index, e)} onFocus={e => e.target.select()} className={`w-12 h-14 text-center text-xl font-semibold border rounded-lg outline-none transition-all ${digit ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]/30' : 'border-gray-300 bg-gray-50 focus:border-[#F7941D] focus:ring-2 focus:ring-[#F7941D]/20 focus:bg-white'}`} data-id="element-9222" />)}
                  </div>
                </div>

                <button type="submit" disabled={!isCodeComplete} className={`w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm ${isCodeComplete ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-9223">
                  <ShieldCheckIcon size={18} className="mr-2" data-id="element-9224" />
                  Verify & Sign In
                </button>
              </form>

              <div className="mt-6 text-center" data-id="element-9225">
                <p className="text-sm text-gray-500" data-id="element-9226">
                  Didn't receive the code?{' '}
                  {resendCountdown > 0 ? <span className="text-gray-400" data-id="element-9227">
                      Resend in {resendCountdown}s
                    </span> : <button type="button" onClick={handleResend} className="text-[#F7941D] hover:text-[#D97B06] font-medium" data-id="element-9228">
                      Resend code
                    </button>}
                </p>
              </div>

              <div className="mt-4 p-3.5 bg-gray-50 rounded-lg border border-gray-100" data-id="element-9229">
                <div className="flex items-start gap-2.5" data-id="element-9230">
                  <ShieldCheckIcon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-9231" />
                  <p className="text-xs text-gray-500 leading-relaxed" data-id="element-9232">
                    For your security, this code expires in 10 minutes. If you
                    didn't request this code, please secure your account
                    immediately.
                  </p>
                </div>
              </div>
            </>}

          <div className="mt-16 pt-6 border-t border-gray-100 text-center lg:text-left" data-id="element-9233">
            <p className="text-xs text-gray-500" data-id="element-9234">
              © 2026 Rabbit Gateway. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>;
}